/**
 * Versioned JSON payloads: a stored or queued `{ ...facts }` carries `v`, and a reader brings any older
 * version up to the current one before using it ("upcasting"). Dependency-free, plain TypeScript with
 * `.ts` import extensions so a Node package and a Deno edge function can both import the source.
 *
 * A payload without `v` is version 1, so introducing the field needs no data migration. See the
 * `versioned-payloads` skill for when to add a version, an optional field, or a new kind instead.
 */

/** Turns a payload at version N into version N + 1. Pure: no I/O, never mutates its input. */
export type Upcaster = (payload: Record<string, unknown>) => Record<string, unknown>;

export interface PayloadSchema {
  /** The version writers produce and readers work with today. */
  current: number;
  /** `upcasters[n]` converts version n to n + 1; one entry per hop below `current`. */
  upcasters?: Readonly<Record<number, Upcaster>>;
}

export type UpcastFailure =
  /** The payload is not an object, or `v` is not a positive integer. */
  | "invalid"
  /** `v` is newer than this reader knows: an old reader meeting data from a newer writer. */
  | "unsupported_version"
  /** A hop between `v` and `current` has no upcaster, or it threw. */
  | "missing_upcaster";

export type UpcastResult =
  | { ok: true; payload: Record<string, unknown>; from: number }
  | { ok: false; reason: UpcastFailure; from?: number };

/** Version of a stored payload: its `v`, or 1 when it has none. */
export function payloadVersion(payload: unknown): number | undefined {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return undefined;
  const v = (payload as Record<string, unknown>).v;
  if (v === undefined) return 1;
  return Number.isInteger(v) && (v as number) >= 1 ? (v as number) : undefined;
}

/**
 * Bring `payload` to `schema.current`. Never throws: a reader decides what to show for a failure
 * (usually a generic fallback) and reports it, instead of rendering half-understood data.
 */
export function upcastPayload(payload: unknown, schema: PayloadSchema): UpcastResult {
  const from = payloadVersion(payload);
  if (from === undefined) return { ok: false, reason: "invalid" };
  if (from > schema.current) return { ok: false, reason: "unsupported_version", from };

  let result = payload as Record<string, unknown>;
  for (let version = from; version < schema.current; version += 1) {
    const hop = schema.upcasters?.[version];
    if (!hop) return { ok: false, reason: "missing_upcaster", from };
    try {
      result = { ...hop(result), v: version + 1 };
    } catch {
      return { ok: false, reason: "missing_upcaster", from };
    }
  }
  return { ok: true, payload: result, from };
}
